import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { VortexWordmark } from "./VortexWordmark";

const TikTokIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.16a8.16 8.16 0 0 0 4.77 1.52V6.69h-1.84Z" /></svg>
);
const FacebookIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95Z" /></svg>
);
const SnapchatIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M12.04 2C8.27 2 7.5 5.2 7.5 7.1c0 .68.1 1.7.2 2.43-.32.12-.64.16-.96.06-.5-.18-.98-.55-1.36-.55-.5 0-.96.4-.96.85 0 .55.66.78 1.32 1.05.9.37 1.9.83 2.36 2.16.06.2.04.42-.06.62-.42.92-1.66 2.78-3.94 3.16-.34.06-.6.4-.6.74 0 .46.34.84.85.94 1.26.24 2.06.42 2.32 1.04.06.13.06.27.04.4-.02.18.08.36.26.46.74.4 2.4.62 2.86.62.16 0 .3-.03.42-.07.46-.16.96-.42 2.86-.42 1.9 0 2.4.26 2.86.42.12.04.26.07.42.07.46 0 2.12-.22 2.86-.62.18-.1.28-.28.26-.46-.02-.13-.02-.27.04-.4.26-.62 1.06-.8 2.32-1.04.5-.1.85-.48.85-.94 0-.34-.26-.68-.6-.74-2.28-.38-3.52-2.24-3.94-3.16-.1-.2-.12-.42-.06-.62.46-1.33 1.46-1.79 2.36-2.16.66-.27 1.32-.5 1.32-1.05 0-.45-.46-.85-.96-.85-.38 0-.86.37-1.36.55-.32.1-.64.06-.96-.06.1-.73.2-1.75.2-2.43C16.5 5.2 15.73 2 12.04 2Z" /></svg>
);

const SocialIconLink = ({ href, label, children, testid }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        className="w-9 h-9 rounded-sm border border-[rgba(0,229,255,0.22)] text-[var(--silver)] hover:border-[var(--cyan)] hover:text-[var(--cyan)] hover:bg-[rgba(0,229,255,0.08)] transition-all inline-flex items-center justify-center"
        data-testid={testid}
    >
        {children}
    </a>
);

export const Footer = () => {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSubmitting(true);
        try {
            const res = await api.post("/newsletter", { email });
            toast.success(res.data.message || "Subscribed");
            setEmail("");
        } catch {
            toast.error("Invalid email address");
        }
        setSubmitting(false);
    };

    const colHeading = "text-[10px] font-mono uppercase tracking-[0.3em] text-[var(--cyan)] mb-5 flex items-center gap-2";
    const colLink = "block text-sm text-[var(--text-secondary)] hover:text-[var(--cyan-hi)] transition-colors";

    return (
        <footer className="relative mt-24 bg-[var(--bg-obsidian)]" data-testid="site-footer">
            {/* Cyan hairline glow above the footer */}
            <div className="cyan-line" />

            {/* Carbon-fibre substrate */}
            <div
                className="absolute inset-0 pointer-events-none opacity-70"
                style={{
                    backgroundImage: `
                        repeating-linear-gradient(45deg,
                            rgba(255,255,255,0.018) 0 2px,
                            rgba(0,0,0,0.35) 2px 4px,
                            rgba(255,255,255,0.018) 4px 6px,
                            rgba(0,0,0,0.35) 6px 8px)
                    `,
                }}
                aria-hidden="true"
            />

            <div className="container-x py-16 relative">
                {/* Tech-tag row */}
                <div className="mb-10 flex flex-wrap items-center gap-3">
                    <span className="cyan-bar" />
                    <span className="tech-label">// Carbon Labs · AU</span>
                    <span className="hidden sm:inline-block h-px w-24 bg-[rgba(0,229,255,0.25)]" />
                    <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-[0.28em] text-white/40">
                        Sydney · NSW · 2000
                    </span>
                </div>

                {/* Brand block on a carbon-fibre panel */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-10">
                    <div className="col-span-2 md:col-span-2 cfibre-panel rounded-sm p-6">
                        <Link to="/" className="inline-flex items-center mb-4" aria-label="Carbon Labs — home">
                            <VortexWordmark
                                size="sm"
                                variant="static"
                                tagline="Premium Research Peptides · AU"
                            />
                        </Link>
                        <p className="text-sm text-[var(--text-secondary)] leading-relaxed max-w-md">
                            Australian-based premium research peptides.
                            Manufacturer-supplied COA available for every product.
                            Cold-chain dispatch from Sydney with discreet packaging.
                        </p>
                        <div className="mt-5 flex flex-wrap gap-2" data-testid="footer-socials">
                            <SocialIconLink
                                href="#" onClick={(e)=>e.preventDefault()} data-brand-todo="tiktok"
                                label="TikTok"
                                testid="footer-social-tiktok"
                            >
                                <TikTokIcon className="w-4 h-4" />
                            </SocialIconLink>
                            <SocialIconLink
                                href="#" onClick={(e)=>e.preventDefault()} data-brand-todo="facebook"
                                label="Facebook"
                                testid="footer-social-facebook"
                            >
                                <FacebookIcon className="w-4 h-4" />
                            </SocialIconLink>
                            <SocialIconLink
                                href="#" onClick={(e)=>e.preventDefault()} data-brand-todo="snapchat"
                                label="Snapchat"
                                testid="footer-social-snapchat"
                            >
                                <SnapchatIcon className="w-4 h-4" />
                            </SocialIconLink>
                            <SocialIconLink
                                href="mailto:carbonlabsau@outlook.com"
                                label="Email"
                                testid="footer-social-email"
                            >
                                <Mail size={16} />
                            </SocialIconLink>
                        </div>
                    </div>

                    <div className="col-span-1">
                        <h4 className={colHeading}>
                            <span className="w-3 h-px bg-[var(--cyan)]" />
                            Shop
                        </h4>
                        <div className="space-y-3">
                            <Link to="/shop" className={colLink} data-testid="footer-shop-products">Products</Link>
                            <Link to="/build-a-stack" className={colLink} data-testid="footer-shop-stack">Build Your Stack</Link>
                            <Link to="/shop?filter=new" className={colLink} data-testid="footer-shop-new">New Arrivals</Link>
                            <Link to="/shop?filter=bestseller" className={colLink} data-testid="footer-shop-best">Best Sellers</Link>
                        </div>
                    </div>

                    <div className="col-span-1">
                        <h4 className={colHeading}>
                            <span className="w-3 h-px bg-[var(--cyan)]" />
                            Support
                        </h4>
                        <div className="space-y-3">
                            <Link to="/contact" className={colLink}>Contact</Link>
                            <Link to="/faq" className={colLink}>FAQs</Link>
                            <Link to="/policies/shipping" className={colLink}>Shipping</Link>
                            <Link to="/policies/returns" className={colLink}>Returns</Link>
                        </div>
                    </div>

                    <div className="col-span-2 md:col-span-1">
                        <h4 className={colHeading}>
                            <span className="w-3 h-px bg-[var(--cyan)]" />
                            Information
                        </h4>
                        <div className="space-y-3">
                            <Link to="/policies/privacy" className={colLink}>Privacy Policy</Link>
                            <Link to="/policies/terms" className={colLink}>Terms &amp; Conditions</Link>
                            <Link to="/lab-standards" className={colLink}>Research Disclaimer</Link>
                        </div>
                    </div>
                </div>

                {/* Newsletter — full-width carbon panel */}
                <div className="mt-12 cfibre-panel-cyan rounded-sm p-6 md:p-7">
                    <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
                        <div className="md:flex-1">
                            <div className="tech-label mb-2">// Signal · Newsletter</div>
                            <h3 className="font-display text-xl md:text-2xl font-bold text-white tracking-tight">
                                Get lot drops, restocks & lab notes
                            </h3>
                            <p className="text-xs text-[var(--text-secondary)] mt-2 max-w-md">
                                One email per fortnight. No pressure, no fluff — new batches, dispatch windows and research
                                references straight to your inbox.
                            </p>
                        </div>
                        <form onSubmit={submit} className="flex gap-2 md:min-w-[360px]" data-testid="footer-newsletter-form">
                            <input
                                type="email"
                                required
                                placeholder="researcher@lab.com.au"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="flex-1 min-w-0 px-4 py-2.5 bg-black/50 border border-[rgba(0,229,255,0.25)] rounded-sm text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--cyan)] focus:shadow-[0_0_0_1px_rgba(0,229,255,0.35)] transition-all font-mono"
                                data-testid="footer-newsletter-email"
                            />
                            <button
                                type="submit"
                                disabled={submitting}
                                className="btn-solid !py-2.5 !px-4"
                                data-testid="footer-newsletter-submit"
                                aria-label="Subscribe"
                            >
                                <Send size={12} /> Subscribe
                            </button>
                        </form>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="mt-12 pt-6 border-t border-[rgba(0,229,255,0.12)] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div className="space-y-1">
                        <p className="text-[10px] text-[var(--text-muted)] font-mono uppercase tracking-[0.25em]">
                            © {new Date().getFullYear()} Carbon Labs — Research use only. Not for human consumption.
                        </p>
                        <p className="text-[10px] text-[var(--text-muted)] font-mono uppercase tracking-[0.25em]">
                            Sydney · NSW · Australia
                        </p>
                    </div>
                    <a
                        href="mailto:carbonlabsau@outlook.com"
                        className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--cyan)] hover:text-[var(--cyan-hi)] transition-colors"
                    >
                        carbonlabsau@outlook.com
                    </a>
                </div>
            </div>
        </footer>
    );
};
